import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "flag-icons/css/flag-icons.min.css";
import axios from "axios";
import { Plus, Loader2, User, Shield, Gamepad2, Trash2, ImagePlus, ChevronDown, Trophy, Play, ArrowDown, Award, Crown, Calendar, Target, Eye, EyeOff, CheckCircle, Search, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FeedbackButtons from "./FeedbackButtons";
import SiteFooter from "./SiteFooter";
import ReferralBanner from "./ReferralBanner";

import { useLanguage } from "@/contexts/LanguageContext";
import PlaceMedal from './PlaceMedal';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Helper to get translated text from translations object
const getTranslatedText = (translations, lang, fallback) => {
  if (!translations) return fallback;
  if (translations[lang]) return translations[lang];
  if (translations['ru']) return translations['ru'];
  return fallback;
};


const GameList = () => {
  const [games, setGames] = useState([]);
  const { t, language } = useLanguage();
  const [winners, setWinners] = useState([]);
  const [allWinners, setAllWinners] = useState([]);
  const [visibleWinnersCount, setVisibleWinnersCount] = useState(10);
  const [achievements, setAchievements] = useState([]);
  // Translations now come from DB, no need for client-side state
  const [gameWinners, setGameWinners] = useState({}); // Winners for each game by game_id
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(null);
  const [draggedGame, setDraggedGame] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [infoSections, setInfoSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  // Info section translations now come from DB
  const [registrationInfo, setRegistrationInfo] = useState({}); // Registration data by game_id
  const [countdowns, setCountdowns] = useState({}); // Countdown timers by game_id
  const [visibleGamesCount, setVisibleGamesCount] = useState(9); // Show 9 games initially
  const [progressFilter, setProgressFilter] = useState('all'); // 'all' | 'new' | 'unfinished' | 'completed'
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [visibleObjectsGamesCount, setVisibleObjectsGamesCount] = useState(9); // For objects-only games
  const [shownCardIndices, setShownCardIndices] = useState(new Set()); // Track cards that have been shown
  const [shownObjectsCardIndices, setShownObjectsCardIndices] = useState(new Set()); // Track objects-only cards
  const [userGameProgress, setUserGameProgress] = useState({}); // User progress for each game
  
  // Section visibility states
  const [gamesVisible, setGamesVisible] = useState(false);
  const [winnersVisible, setWinnersVisible] = useState(false);
  const [achievementsVisible, setAchievementsVisible] = useState(false);
  const [infoVisible, setInfoVisible] = useState(false);
  
  const navigate = useNavigate();
  const { user, hasRole, getAuthHeader } = useAuth();

  // Helpers for "progress filter" chips ('all' | 'new' | 'unfinished' | 'completed')
  const isCompletedGame = (game) => !!userGameProgress[game.id]?.isCompleted;
  const isUnfinished = (game) => {
    const p = userGameProgress[game.id];
    if (!p) return false;
    if (p.isCompleted) return false;
    return (p.panoramasAnswered || 0) > 0 || (p.hotspotsFound || 0) > 0;
  };
  const isNewGame = (game) => {
    const p = userGameProgress[game.id];
    if (!p) return true;
    if (p.isCompleted) return false;
    return (p.panoramasAnswered || 0) === 0 && (p.hotspotsFound || 0) === 0;
  };
  const matchesProgressFilter = (game) => {
    if (progressFilter === 'all') return true;
    if (progressFilter === 'new') return isNewGame(game);
    if (progressFilter === 'unfinished') return isUnfinished(game);
    if (progressFilter === 'completed') return isCompletedGame(game);
    return true;
  };

  // Note: chip count is shown only after progress fully loads (no layout shift).
  
  const gamesRef = useRef(null);
  const gamesSectionRef = useRef(null);
  const winnersSectionRef = useRef(null);
  const achievementsSectionRef = useRef(null);
  const infoSectionRef = useRef(null);
  const earthContainerRef = useRef(null);
  const earthInstanceRef = useRef(null);

  // Load Miniature Earth globe (local commercial version)
  // Use global instance to avoid recreation issues with this library
  useEffect(() => {
    const basePath = window.location.pathname.startsWith('/game') ? '/game' : '';
    const containerId = 'miniature-earth-globe';
    
    function initEarth() {
      const container = document.getElementById(containerId);
      if (!container) {
        setTimeout(initEarth, 100);
        return;
      }
      
      // Check if globe already exists in this container
      if (container.querySelector('canvas')) {
        return; // Globe already initialized
      }
      
      // Destroy any existing global instance first
      if (window._earthInstance) {
        try {
          window._earthInstance.destroy();
        } catch (e) {}
        window._earthInstance = null;
      }
      
      if (window.Earth) {
        try {
          window._earthInstance = new window.Earth(container, {
            location: { lat: 20, lng: 20 },
            light: 'none',
            mapImage: `${basePath}/demo/hologram/hologram-map.svg`,
            transparent: true,
            autoRotate: true,
            autoRotateSpeed: 0.8,
            autoRotateDelay: 100,
            autoRotateStart: 500
          });
        } catch (e) {
          console.warn('Failed to initialize Earth globe:', e);
        }
      }
    }

    // Check if library already loaded
    if (window.Earth) {
      initEarth();
    } else {
      const script = document.createElement('script');
      script.src = `${basePath}/miniature.earth.core.js`;
      script.async = true;
      script.onload = initEarth;
      script.onerror = () => console.warn('Miniature Earth not available');
      document.head.appendChild(script);
    }

    // No cleanup - let the globe persist to avoid classList errors
    return () => {};
  }, []);

  // Intersection observers - cards fly in when visible, fly out when less visible
  useEffect(() => {
    // Check if mobile (viewport width < 768px)
    const isMobile = window.innerWidth < 768;
    
    // Use lower threshold for mobile (20%) vs desktop (50%)
    const visibilityThreshold = isMobile ? 0.15 : 0.5;
    
    const options = { 
      threshold: [0, 0.1, 0.15, 0.2, 0.3, 0.4, 0.5, 0.6],
      rootMargin: isMobile ? "50px 0px" : "0px"  // Extra margin for mobile
    };
    
    const createObserver = (setVisible) => new IntersectionObserver(([entry]) => {
      // Fly-in when threshold+ is visible, fly-out when less
      if (entry.intersectionRatio >= visibilityThreshold) {
        setVisible(true);
      } else if (entry.intersectionRatio < visibilityThreshold) {
        setVisible(false);
      }
    }, options);

    const gamesObserver = createObserver(setGamesVisible);
    const winnersObserver = createObserver(setWinnersVisible);
    const achievementsObserver = createObserver(setAchievementsVisible);
    const infoObserver = createObserver(setInfoVisible);

    if (gamesSectionRef.current) gamesObserver.observe(gamesSectionRef.current);
    if (winnersSectionRef.current) winnersObserver.observe(winnersSectionRef.current);
    if (achievementsSectionRef.current) achievementsObserver.observe(achievementsSectionRef.current);
    if (infoSectionRef.current) infoObserver.observe(infoSectionRef.current);
    
    // Check initial visibility on mount (for cases when page loads scrolled to cards)
    const checkInitialVisibility = (ref, setVisible) => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport) {
          setVisible(true);
        }
      }
    };
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      checkInitialVisibility(gamesSectionRef, setGamesVisible);
      checkInitialVisibility(winnersSectionRef, setWinnersVisible);
      checkInitialVisibility(achievementsSectionRef, setAchievementsVisible);
      checkInitialVisibility(infoSectionRef, setInfoVisible);
    }, 100);

    return () => {
      gamesObserver.disconnect();
      winnersObserver.disconnect();
      achievementsObserver.disconnect();
      infoObserver.disconnect();
    };
  }, [loading, winners.length, achievements.length, infoSections.length]);

  useEffect(() => {
    fetchGames();
    fetchInfoSections();
    fetchTopWinners();
    fetchRecentAchievements();
  }, [user?.id]);

  // Translations now loaded from DB with game data - no client-side translation needed

  // Info section translations now loaded from DB

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/games`, user ? { headers: getAuthHeader() } : {});
      setGames(response.data);
      // Fetch winners for all games
      fetchAllGameWinners(response.data);
      // Fetch registration info for paid games
      fetchRegistrationInfo(response.data);
    } catch (error) {
      console.error("Error fetching games:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user progress for all games (parallel + incremental state updates)
  useEffect(() => {
    if (!user || games.length === 0) return;
    const headers = getAuthHeader();

    const fetchOneGame = async (game) => {
      try {
        const isObjectsOnly = game.game_mode === 'objects_only';

        // Step 1: panoramas list + user's game results in parallel
        const [panoramasResp, resultsResp] = await Promise.all([
          axios.get(`${API}/games/${game.id}/panoramas`).catch(() => ({ data: [] })),
          axios.get(`${API}/games/${game.id}/my-results`, { headers })
            .catch(() => ({ data: { answered_count: 0 } })),
        ]);
        const panoramas = panoramasResp.data || [];
        const answeredCount = resultsResp.data?.answered_count || 0;

        // Step 2: hotspots + found-hotspots for ALL panoramas of this game — in parallel
        const hotspotPairs = await Promise.all(
          panoramas.map((p) => Promise.all([
            axios.get(`${API}/panoramas/${p.id}/hotspots`).catch(() => ({ data: [] })),
            axios.get(`${API}/panoramas/${p.id}/hotspots/found`, { headers })
              .catch(() => ({ data: { found_hotspot_ids: [] } })),
          ]))
        );
        let totalHotspots = 0;
        let foundHotspots = 0;
        for (const [hsResp, foundResp] of hotspotPairs) {
          totalHotspots += (hsResp.data || []).length;
          foundHotspots += (foundResp.data?.found_hotspot_ids || []).length;
        }

        const panoramasTotal = isObjectsOnly ? 0 : panoramas.length;
        const panoramasAnswered = isObjectsOnly ? 0 : answeredCount;
        const panoramasComplete = isObjectsOnly || (panoramasTotal > 0 && panoramasAnswered >= panoramasTotal);
        const hotspotsComplete = totalHotspots === 0 || foundHotspots >= totalHotspots;
        const isCompleted = panoramasComplete && hotspotsComplete;
        const hasStarted = answeredCount > 0 || foundHotspots > 0;

        if (hasStarted) {
          // Incremental update: each game's progress lands in state the moment it's computed
          setUserGameProgress((prev) => ({
            ...prev,
            [game.id]: {
              panoramasTotal,
              panoramasAnswered,
              hotspotsTotal: totalHotspots,
              hotspotsFound: foundHotspots,
              isCompleted,
              isObjectsOnly,
            },
          }));
        }
      } catch (error) {
        console.error(`Error fetching progress for game ${game.id}:`, error);
      }
    };

    // Reset progress when user/games change, then fetch all games in parallel
    setUserGameProgress({});
    setProgressLoaded(false);
    Promise.all(games.map(fetchOneGame)).finally(() => setProgressLoaded(true));
  }, [user, games, getAuthHeader]);

  // Fetch registration info for games with points_required > 0
  const fetchRegistrationInfo = async (gamesList) => {
    const paidGames = gamesList.filter(g => g.points_required > 0 || g.min_participants > 0);
    const regInfoMap = {};
    
    for (const game of paidGames) {
      try {
        const headers = user ? getAuthHeader() : {};
        const resp = await axios.get(`${API}/games/${game.id}/registration-info`, { headers });
        regInfoMap[game.id] = resp.data;
      } catch (err) {
        console.error(`Error fetching registration info for ${game.id}:`, err);
      }
    }
    
    setRegistrationInfo(regInfoMap);
  };

  // Countdown timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      const newCountdowns = {};
      games.forEach(game => {
        if (game.registration_deadline) {
          const deadline = new Date(game.registration_deadline);
          const now = new Date();
          const diff = deadline - now;
          
          if (diff > 0) {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            
            newCountdowns[game.id] = { days, hours, minutes, seconds, expired: false };
          } else {
            newCountdowns[game.id] = { expired: true };
          }
        }
      });
      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [games]);

  const fetchInfoSections = async () => {
    try {
      const response = await axios.get(`${API}/info-sections`);
      setInfoSections(response.data);
    } catch (error) {
      console.error("Error fetching info sections:", error);
    }
  };

  const fetchTopWinners = async () => {
    try {
      const response = await axios.get(`${API}/top-winners?limit=100`);
      setAllWinners(response.data);
      setWinners(response.data.slice(0, 10));
    } catch (error) {
      console.error("Error fetching winners:", error);
    }
  };

  const fetchRecentAchievements = async () => {
    try {
      const response = await axios.get(`${API}/recent-achievements?limit=6`);
      setAchievements(response.data);
    } catch (error) {
      console.error("Error fetching achievements:", error);
    }
  };

  // Fetch winners for all games (for medal display)
  const fetchAllGameWinners = async (gamesList) => {
    const winnersMap = {};
    for (const game of gamesList) {
      try {
        const response = await axios.get(`${API}/games/${game.id}/winners`);
        winnersMap[game.id] = response.data || [];
      } catch (error) {
        winnersMap[game.id] = [];
      }
    }
    setGameWinners(winnersMap);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const scrollToGames = () => {
    gamesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDeleteGame = async (gameId, e) => {
    e.stopPropagation();
    if (!window.confirm('Вы уверены, что хотите удалить эту игру?')) return;
    setDeleting(gameId);
    try {
      await axios.delete(`${API}/games/${gameId}`, { headers: getAuthHeader() });
      toast.success(t('toast.gameDeleted'));
      fetchGames();
    } catch (error) {
      toast.error(t('toast.deleteError'));
    } finally {
      setDeleting(null);
    }
  };

  const handleCoverUpload = async (gameId, file, e) => {
    e.stopPropagation();
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('errors.invalidImageFormat'));
      return;
    }
    
    setUploadingCover(gameId);
    const formData = new FormData();
    formData.append('cover', file);  // Must match backend parameter name
    try {
      await axios.post(`${API}/games/${gameId}/cover`, formData, {
        headers: getAuthHeader(),  // Don't set Content-Type for FormData
        timeout: 60000
      });
      toast.success(t('toast.imageUploaded'));
      fetchGames();
    } catch (error) {
      console.error('Cover upload error:', error);
      toast.error(error.response?.data?.detail || t('toast.saveError'));
    } finally {
      setUploadingCover(null);
    }
  };

  const handleDragStart = (e, game, index) => {
    if (!hasRole(['admin', 'editor'])) return;
    setDraggedGame({ game, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedGame && draggedGame.index !== index) setDragOverIndex(index);
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (!draggedGame || draggedGame.index === targetIndex) {
      setDraggedGame(null);
      setDragOverIndex(null);
      return;
    }
    const newGames = [...games];
    const [removed] = newGames.splice(draggedGame.index, 1);
    newGames.splice(targetIndex, 0, removed);
    setGames(newGames);
    try {
      await axios.put(`${API}/games/reorder`, { 
        game_ids: newGames.map(g => g.id) 
      }, { headers: getAuthHeader() });
    } catch (error) {
      console.error('Reorder error:', error);
      fetchGames();
    }
    setDraggedGame(null);
    setDragOverIndex(null);
  };

  // Build full avatar URL from relative path
  const buildAvatarUrl = (relativePath) => {
    if (!relativePath) return null;
    // If path starts with /api, prepend BACKEND_URL
    if (relativePath.startsWith('/api')) {
      return `${BACKEND_URL}${relativePath}`;
    }
    // If it's already a full URL, return as is
    if (relativePath.startsWith('http')) {
      return relativePath;
    }
    // Otherwise prepend API base
    return `${API}${relativePath}`;
  };

  // Generate DiceBear cartoon avatar URL as fallback
  const getDiceBearUrl = (seed) => {
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed || 'default')}&backgroundColor=667eea`;
  };

  // Get avatar URL - prioritize avatar_url, then full_image_url, then DiceBear
  const getWinnerAvatarUrl = (winner) => {
    // Priority 1: avatar_url (uploaded avatar)
    if (winner.avatar_url) {
      return buildAvatarUrl(winner.avatar_url);
    }
    // Priority 2: full_image_url (real photo)
    if (winner.full_image_url) {
      return buildAvatarUrl(winner.full_image_url);
    }
    // Priority 3: DiceBear cartoon avatar based on user name
    return getDiceBearUrl(winner.user_name || winner.user_id);
  };
  
  // Get avatar for achievements - same logic with DiceBear fallback
  const getAchievementAvatarUrl = (achievement) => {
    if (achievement.avatar_url) {
      return buildAvatarUrl(achievement.avatar_url);
    }
    if (achievement.full_image_url) {
      return buildAvatarUrl(achievement.full_image_url);
    }
    // DiceBear fallback
    return getDiceBearUrl(achievement.user_full_name || achievement.user_id);
  };

  // Get game cover URL
  const getGameCoverUrl = (item) => {
    if (item.game_cover) {
      if (item.game_cover.startsWith('/var/lib/game/image/game_covers/')) {
        return `${API}/games/${item.game_id}/cover`;
      }
      return item.game_cover;
    }
    return null;
  };

  const getPlaceIcon = (index) => {
    if (index === 0) return <Crown size={32} style={{ color: '#fbbf24' }} />;
    if (index === 1) return <Award size={26} style={{ color: '#94a3b8' }} />;
    if (index === 2) return <Award size={26} style={{ color: '#d97706' }} />;
    return <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#6b7280' }}>#{index + 1}</span>;
  };

  const getPlaceStyle = (index) => {
    if (index === 0) return { 
      background: 'linear-gradient(145deg, #fef9c3 0%, #fde047 100%)', 
      border: '3px solid #eab308',
      boxShadow: '0 20px 60px rgba(234, 179, 8, 0.4)'
    };
    if (index === 1) return { 
      background: 'linear-gradient(145deg, #f1f5f9 0%, #cbd5e1 100%)', 
      border: '2px solid #94a3b8',
      boxShadow: '0 15px 40px rgba(148, 163, 184, 0.3)'
    };
    if (index === 2) return { 
      background: 'linear-gradient(145deg, #ffedd5 0%, #fed7aa 100%)', 
      border: '2px solid #ea580c',
      boxShadow: '0 15px 40px rgba(234, 88, 12, 0.25)'
    };
    return { background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' };
  };

  // Animation styles - fly out when leaving
  const getGameCardStyle = (index, isVisible) => {
    // Once a card has been shown, always show it (prevents fly-out on "show more")
    const alreadyShown = shownCardIndices.has(index);
    const shouldShow = isVisible || alreadyShown;
    
    const delay = shouldShow ? Math.min(index, 8) * 0.1 : 0; // Cap delay at 0.8s for new cards
    const direction = index % 3;
    let transform;
    if (direction === 0) transform = 'translateX(-150vw) rotate(-25deg) scale(0.3)';
    else if (direction === 1) transform = 'translateY(150vh) rotate(20deg) scale(0.3)';
    else transform = 'translateX(150vw) rotate(25deg) scale(0.3)';
    
    return {
      opacity: shouldShow ? 1 : 0,
      transform: shouldShow ? 'translateX(0) translateY(0) rotate(0deg) scale(1)' : transform,
      transition: shouldShow 
        ? `all 1s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
        : 'none'
    };
  };
  
  // Style for objects-only cards
  const getObjectsCardStyle = (index, isVisible) => {
    const alreadyShown = shownObjectsCardIndices.has(index);
    const shouldShow = isVisible || alreadyShown;
    
    const delay = shouldShow ? Math.min(index, 8) * 0.1 : 0;
    const direction = index % 3;
    let transform;
    if (direction === 0) transform = 'translateX(-150vw) rotate(-25deg) scale(0.3)';
    else if (direction === 1) transform = 'translateY(150vh) rotate(20deg) scale(0.3)';
    else transform = 'translateX(150vw) rotate(25deg) scale(0.3)';
    
    return {
      opacity: shouldShow ? 1 : 0,
      transform: shouldShow ? 'translateX(0) translateY(0) rotate(0deg) scale(1)' : transform,
      transition: shouldShow 
        ? `all 1s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
        : 'none'
    };
  };
  
  // Track shown cards when gamesVisible changes or more cards are shown
  useEffect(() => {
    if (gamesVisible) {
      setShownCardIndices(prev => {
        const newSet = new Set(prev);
        // Add indices for location games
        const locationGames = games.filter(g => g.game_mode !== 'objects_only');
        for (let i = 0; i < visibleGamesCount && i < locationGames.length; i++) {
          newSet.add(i);
        }
        return newSet;
      });
      // Also update objects-only cards
      setShownObjectsCardIndices(prev => {
        const newSet = new Set(prev);
        const objectsGames = games.filter(g => g.game_mode === 'objects_only');
        for (let i = 0; i < visibleObjectsGamesCount && i < objectsGames.length; i++) {
          newSet.add(i);
        }
        return newSet;
      });
    }
  }, [gamesVisible, visibleGamesCount, visibleObjectsGamesCount, games]);

  const getWinnerCardStyle = (index, isVisible) => {
    const delay = isVisible ? 0.1 + index * 0.12 : 0;
    const angle = (index % 2 === 0 ? -1 : 1) * 30;
    const xOffset = (index % 2 === 0 ? -1 : 1) * 120;
    return {
      opacity: isVisible ? 1 : 0,
      transform: isVisible 
        ? 'translateY(0) translateX(0) scale(1) rotate(0deg)' 
        : `translateY(200px) translateX(${xOffset}vw) scale(0.2) rotate(${angle}deg)`,
      transition: isVisible 
        ? `all 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}s`
        : `all 0.7s cubic-bezier(0.7, 0, 0.84, 0) ${(winners.length - index) * 0.08}s`
    };
  };

  const getAchievementCardStyle = (index, isVisible) => {
    const delay = isVisible ? index * 0.08 : 0;
    const xOffset = (index % 2 === 0 ? -1 : 1) * 120;
    return {
      opacity: isVisible ? 1 : 0,
      transform: isVisible 
        ? 'translateX(0) translateY(0) scale(1)' 
        : `translateX(${xOffset}vw) translateY(80px) scale(0.4)`,
      transition: isVisible 
        ? `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
        : `all 0.6s cubic-bezier(0.7, 0, 0.84, 0) ${(achievements.length - index) * 0.06}s`
    };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const monthKeys = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const monthName = t(`months.${monthKeys[monthIndex]}`);
    return `${day} ${monthName}`;
  };

  const getPlaceEmoji = (place) => {
    if (place === 1 || place === 2 || place === 3) {
      return <PlaceMedal place={place} size="sm" />;
    }
    return `#${place}`;
  };

  return (
    <div style={{ background: '#0a0a1a', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <section className="hero-section" style={{ background: '#0a0a1a' }}>
        {/* Earth Globe Background - Miniature Earth Hologram */}
        <div 
          ref={earthContainerRef}
          id="miniature-earth-globe"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100vmax',
            height: '100vmax',
            zIndex: 1
          }}
        />

        <div className="hero-content" style={{ 
          position: 'relative', 
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}>
          <ReferralBanner />
          <img 
            src="/game/cs_logo.png" 
            alt="Country Scanner" 
            className="hero-logo"
            style={{
              maxWidth: '400px',
              width: '100%',
              height: 'auto',
              marginBottom: '1.5rem',
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          />
          <p className="hero-subtitle">
            {t("hero.tagline1")}<br />
            {t("hero.tagline2")}
          </p>
          
          <div className="hero-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
            <Button onClick={scrollToGames} size="lg" className="btn-play">
              <Play size={22} style={{ marginRight: '0.5rem' }} />
              {t("game.startGame")}
            </Button>
            {user && (
              <button
                type="button"
                data-testid="invite-friend-hero-btn"
                className="btn-invite-hero"
                onClick={() => navigate('/virality')}
                title={t('virality.inviteFriendTooltip')}
              >
                <Share2 size={22} style={{ marginRight: '0.5rem' }} />
                {t('virality.inviteFriend')}
              </button>
            )}
          </div>
        </div>

        <div 
          onClick={scrollToGames} 
          className="scroll-indicator" 
          style={{ 
            position: 'absolute', 
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            cursor: 'pointer'
          }}
        >
          <ArrowDown size={36} color="white" />
        </div>
      </section>

      {/* Games Section - Title */}
      <section ref={gamesSectionRef} className="section games-section">
        <div className="container">
          <div className="games-header">
            <h2 className="section-title">{t("game.selectGame")}</h2>
            {user && (
              <div className="progress-filter-chips" role="tablist" aria-label={t('gameListUI.progressFilter')}>
                {(() => {
                  const counts = {
                    all: games.length,
                    new: games.filter(isNewGame).length,
                    unfinished: games.filter(isUnfinished).length,
                    completed: games.filter(isCompletedGame).length,
                  };
                  const chipDefs = [
                    { key: 'all', label: t('gameListUI.filterAll') },
                    { key: 'new', label: t('gameListUI.filterNew') },
                    { key: 'unfinished', label: t('gameListUI.filterUnfinished') },
                    { key: 'completed', label: t('gameListUI.filterCompleted') },
                  ];
                  return chipDefs.map(({ key, label }) => {
                    const hasCountSlot = key !== 'all';
                    const showNumber = hasCountSlot && progressLoaded && counts[key] > 0;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={progressFilter === key}
                        data-testid={`progress-chip-${key}`}
                        className={`progress-chip ${progressFilter === key ? 'is-active' : ''}`}
                        onClick={() => setProgressFilter(key)}
                      >
                        {label}
                        {hasCountSlot && (
                          <span
                            className="progress-chip-count"
                            aria-hidden={!showNumber}
                            style={{ visibility: showNumber ? 'visible' : 'hidden' }}
                          >
                            {showNumber ? counts[key] : '0'}
                          </span>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Location + Objects Section - Full Width */}
      <section style={{
        background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #e0e7ff 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '3rem 0'
      }}>
        {/* Decorative geometric shapes */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '120px',
          height: '120px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '70px',
          height: '70px',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.05) 100%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '3%',
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(167, 139, 250, 0.05) 100%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          top: '30%',
          left: '8%',
          width: '40px',
          height: '40px',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '8px',
          transform: 'rotate(45deg)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '40%',
          right: '25%',
          width: '30px',
          height: '30px',
          background: 'rgba(99, 102, 241, 0.12)',
          borderRadius: '6px',
          transform: 'rotate(15deg)'
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <h3 style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            textAlign: 'center',
            marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
            letterSpacing: '0.5px'
          }}>
            <span style={{ marginRight: '0.5rem', WebkitTextFillColor: 'initial', background: 'none', WebkitBackgroundClip: 'initial', backgroundClip: 'initial', textShadow: 'none', display: 'inline-block' }}>📍</span>
            {t("game.locationAndObjects")}
            <span style={{ marginLeft: '0.5rem', WebkitTextFillColor: 'initial', background: 'none', WebkitBackgroundClip: 'initial', backgroundClip: 'initial', textShadow: 'none', display: 'inline-block' }}>🎯</span>
          </h3>

          <div ref={gamesRef}>
            {loading ? (
              <div className="loading-state">
                <Loader2 size={56} className="animate-spin" style={{ color: '#667eea' }} />
              </div>
            ) : games.length === 0 ? (
              <div className="empty-state">
                <Gamepad2 size={72} />
                <p>{t('gameListUI.thereNoGamesYet')}</p>
              </div>
            ) : (
              <>
              {progressFilter !== 'all'
                && games.filter(g => g.game_mode !== 'objects_only').filter(g => matchesProgressFilter(g)).length === 0
                && games.filter(g => g.game_mode === 'objects_only').filter(g => matchesProgressFilter(g)).length === 0 && (
                <div className="empty-state" data-testid="progress-filter-empty" style={{ padding: '2rem 1rem', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                    {progressFilter === 'unfinished' && 'У вас нет начатых незавершённых игр'}
                    {progressFilter === 'completed' && 'У вас пока нет завершённых игр'}
                    {progressFilter === 'new' && 'Новых игр для вас нет — все уже начаты или пройдены'}
                  </p>
                  <p style={{ fontSize: '0.9rem' }}>{t('gameListUI.selectAllSeeAll')}</p>
                </div>
              )}
              <div className="games-grid" key={`pano-${progressFilter}`}>
                {games.filter(g => g.game_mode !== 'objects_only').filter(g => matchesProgressFilter(g)).slice(0, visibleGamesCount).map((game, filteredIndex) => { const realIndex = games.findIndex(g => g.id === game.id); return (
                  <div key={game.id} style={getGameCardStyle(filteredIndex, gamesVisible)}>
                    <div
                      onClick={() => navigate(`/${game.id}`)}
                      draggable={hasRole(['admin', 'editor'])}
                      onDragStart={(e) => handleDragStart(e, game, realIndex)}
                      onDragOver={(e) => handleDragOver(e, realIndex)}
                      onDrop={(e) => handleDrop(e, realIndex)}
                      onDragEnd={() => { setDraggedGame(null); setDragOverIndex(null); }}
                      className="game-card-full"
                      style={{
                        background: game.cover_image_path 
                          ? `linear-gradient(to bottom, rgba(0,0,0,${userGameProgress[game.id]?.isCompleted ? '0.65' : '0.1'}) 0%, rgba(0,0,0,${userGameProgress[game.id]?.isCompleted ? '0.9' : '0.75'}) 100%), url(${API}/games/${game.id}/cover?thumb=true) center/cover`
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: dragOverIndex === realIndex 
                          ? '0 0 0 4px #667eea, 0 25px 60px rgba(102, 126, 234, 0.5)'
                          : undefined
                      }}
                    >


                      {/* Progress + interactive video — grouped at top-left */}
                      {(game.has_interactive_video || userGameProgress[game.id]) && (
                        <div className="progress-video-group">
                          {game.has_interactive_video && (
                            <div className="interactive-video-icon" data-testid="interactive-video-badge" title={t('gameListUI.interactiveVideo')}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            </div>
                          )}
                          {userGameProgress[game.id]?.isCompleted ? (
                            <div className="progress-badge-inline completed" title={t('gameListUI.completed')}>
                              <CheckCircle size={12} />
                              <span>{t('gameListUI.completed2')}</span>
                            </div>
                          ) : userGameProgress[game.id] && (
                            <>
                              {!userGameProgress[game.id].isObjectsOnly && userGameProgress[game.id].panoramasTotal > 0 && (
                                <div className="progress-badge-inline panoramas" title={t('gameListUI.panoramas')}>
                                  <Target size={12} />
                                  <span>{userGameProgress[game.id].panoramasAnswered}/{userGameProgress[game.id].panoramasTotal}</span>
                                </div>
                              )}
                              {userGameProgress[game.id].hotspotsTotal > 0 && (
                                <div className="progress-badge-inline hotspots" title={t('gameListUI.objects')}>
                                  <Search size={12} />
                                  <span>{userGameProgress[game.id].hotspotsFound}/{userGameProgress[game.id].hotspotsTotal}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Admin actions — uniform group at bottom-right (inline with date) */}
                      {hasRole(['admin', 'editor']) && (
                        <div className="admin-actions-overlay">
                          <div
                            title={game.published ? 'Опубликовано' : 'Черновик'}
                            className="game-admin-btn"
                            style={{ color: game.published ? '#10b981' : '#f59e0b' }}
                          >
                            {game.published ? '✓' : '✎'}
                          </div>
                          {hasRole(['admin']) && (
                            <button
                              onClick={(e) => handleDeleteGame(game.id, e)}
                              disabled={deleting === game.id}
                              className="game-admin-btn btn-delete"
                              title={t('gameListUI.remove')}
                            >
                              {deleting === game.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            </button>
                          )}
                          <label onClick={e => e.stopPropagation()} className="game-admin-btn" title={t('gameListUI.uploadCover')}>
                            {uploadingCover === game.id ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <>
                                <ImagePlus size={13} />
                                <input type="file" accept="image/*" style={{ display: 'none' }}
                                  onChange={(e) => handleCoverUpload(game.id, e.target.files[0], e)} />
                              </>
                            )}
                          </label>
                        </div>
                      )}
                      
                      {/* Players badge */}
                      {game.players_count > 0 && (
                        <div className="players-badge-overlay">
                          <User size={14} />{game.players_count}
                        </div>
                      )}
                      
                      {/* Paid game badge - next to players badge (hide for collecting games and games with min_participants) */}
                      {game.points_required > 0 && game.game_status !== 'collecting' && !(game.min_participants > 0) && (
                        <div className="points-badge-overlay" style={{
                          left: game.players_count > 0 ? 'calc(1rem + 75px)' : '1rem'
                        }}>
                          ⭐ {game.points_required}
                        </div>
                      )}

                      {/* Registration info - CENTERED for collecting/paid games */}
                      {/* Status badge - TOP CENTER of card when min participants reached */}
                      {game.min_participants > 0 && registrationInfo[game.id]?.registered_count >= game.min_participants && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(34, 197, 94, 0.95)',
                            color: 'white',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            zIndex: 15,
                            boxShadow: '0 2px 10px rgba(34, 197, 94, 0.5)',
                            cursor: 'help'
                          }}
                          title={t('gameListUI.requiredNumberPlayersBeen')}
                        >
                          ✓
                        </div>
                      )}

                      {/* Registration info - CENTER for collecting/paid games */}
                      {(game.game_status === 'collecting' || (game.points_required > 0 && game.min_participants > 0)) && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '10px',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.2)',
                          borderRadius: '10px',
                          padding: '8px 10px',
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-around',
                          gap: '8px',
                          backdropFilter: 'blur(4px)'
                        }}>
                          {/* Timer - show if deadline exists */}
                          {game.registration_deadline && (
                            <div style={{ textAlign: 'center' }} title={t('gameListUI.gamePublicationTime')}>
                              {countdowns[game.id] && !countdowns[game.id].expired ? (
                                <div style={{ color: '#fbbf24', fontSize: '0.95rem', fontWeight: 'bold', fontFamily: 'monospace', cursor: 'help' }}>
                                  ⏱️ {countdowns[game.id].days > 0 && `${countdowns[game.id].days}д `}
                                  {String(countdowns[game.id].hours).padStart(2, '0')}:
                                  {String(countdowns[game.id].minutes).padStart(2, '0')}:
                                  {String(countdowns[game.id].seconds).padStart(2, '0')}
                                </div>
                              ) : (
                                <div style={{ color: '#22c55e', fontSize: '0.95rem', fontWeight: 'bold', cursor: 'pointer' }}>{t('gameListUI.play')}</div>
                              )}
                            </div>
                          )}
                          
                          {/* Cost */}
                          <div style={{ textAlign: 'center' }} title={t('gameListUI.costParticipation')}>
                            <div style={{ color: '#fbbf24', fontSize: '0.95rem', fontWeight: 'bold', cursor: 'help' }}>
                              ⭐ {game.points_required}
                            </div>
                          </div>
                          
                          {/* Participants with progress bar */}
                          {game.min_participants > 0 && (
                            <div style={{ textAlign: 'center', minWidth: '65px' }} title={t('gameListUI.participantsSubmittedRequired')}>
                              <div style={{ 
                                color: registrationInfo[game.id]?.registered_count >= game.min_participants ? '#22c55e' : 'white', 
                                fontSize: '0.85rem', 
                                fontWeight: 'bold',
                                marginBottom: '3px',
                                cursor: 'help'
                              }}>
                                👥 {registrationInfo[game.id]?.registered_count || 0}/{game.min_participants}
                              </div>
                              <div style={{
                                background: 'rgba(255,255,255,0.3)',
                                borderRadius: '3px',
                                height: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  background: registrationInfo[game.id]?.registered_count >= game.min_participants 
                                    ? '#22c55e' 
                                    : '#fbbf24',
                                  height: '100%',
                                  width: `${Math.min(100, ((registrationInfo[game.id]?.registered_count || 0) / game.min_participants) * 100)}%`,
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Medals - bottom right corner */}
                      <div className="medals-overlay">
                        {[1, 2, 3].map((place) => {
                          const winnersForGame = gameWinners[game.id] || [];
                          const winner = winnersForGame.find(w => w.place === place);
                          const isOccupied = !!winner;
                          const placeLabel = place === 1 ? '1-е место' : place === 2 ? '2-е место' : '3-е место';
                          const tooltip = (isOccupied && winner)
                            ? `${placeLabel}: ${winner.user_full_name} — ${winner.percentage?.toFixed(1)}%`
                            : `${placeLabel} — свободно`;
                          const medalEmoji = (
                            <PlaceMedal
                              place={place}
                              height={64}
                              title={tooltip}
                            />
                          );

                          return (
                            <span
                              key={place}
                              className={`medal ${isOccupied ? 'medal-occupied' : 'medal-empty'}`}
                              onClick={(e) => {
                                if (isOccupied && winner) {
                                  e.stopPropagation();
                                  navigate(`/${game.id}/user-results/${winner.user_id}`);
                                }
                              }}
                            >
                              {medalEmoji}
                            </span>
                          );
                        })}
                      </div>
                      
                      {/* Content overlay */}
                      <div className="game-overlay">
                        <h3 className="game-title-overlay">{getTranslatedText(game.name_translations, language, game.name)}</h3>
                        <p className="game-date-overlay">{formatDate(game.published && game.published_at ? game.published_at : game.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ); })}
              </div>
              {games.filter(g => g.game_mode !== 'objects_only').filter(g => matchesProgressFilter(g)).length > visibleGamesCount && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginTop: '2rem' 
                }}>
                  <button
                    onClick={() => { const newCount = visibleGamesCount + 9; setShownCardIndices(prev => { const s = new Set(prev); for(let i=visibleGamesCount; i<newCount; i++) s.add(i); return s; }); setVisibleGamesCount(newCount); }}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 32px',
                      borderRadius: '25px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    {t('game.showMore')} ({games.filter(g => g.game_mode !== 'objects_only').filter(g => matchesProgressFilter(g)).length - visibleGamesCount})
                  </button>
                </div>
              )}
            </>
            )}
          </div>
        </div>
      </section>

      {/* Objects Only Section - Full Width */}
      {games.filter(g => g.game_mode === 'objects_only').filter(g => matchesProgressFilter(g)).length > 0 && (
      <section style={{
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fef9c3 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '3rem 0'
      }}>
        {/* Decorative geometric shapes */}
        <div style={{
          position: 'absolute',
          top: '15%',
          left: '5%',
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.05) 100%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '25%',
          right: '8%',
          width: '70px',
          height: '70px',
          background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.15) 0%, rgba(217, 119, 6, 0.05) 100%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          top: '50%',
          right: '20%',
          width: '50px',
          height: '50px',
          background: 'rgba(251, 191, 36, 0.15)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '12%',
          width: '35px',
          height: '35px',
          background: 'rgba(245, 158, 11, 0.12)',
          borderRadius: '6px',
          transform: 'rotate(30deg)'
        }} />
        <div style={{
          position: 'absolute',
          top: '25%',
          right: '35%',
          width: '25px',
          height: '25px',
          background: 'rgba(217, 119, 6, 0.1)',
          borderRadius: '4px',
          transform: 'rotate(-15deg)'
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <h3 style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            textAlign: 'center',
            marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textShadow: '0 2px 10px rgba(245, 158, 11, 0.3)',
            letterSpacing: '0.5px'
          }}>
            <span style={{ marginRight: '0.5rem', WebkitTextFillColor: 'initial', background: 'none', WebkitBackgroundClip: 'initial', backgroundClip: 'initial', textShadow: 'none', display: 'inline-block' }}>🔍</span>
            {t("game.objectsOnly")}
            <span style={{ marginLeft: '0.5rem', WebkitTextFillColor: 'initial', background: 'none', WebkitBackgroundClip: 'initial', backgroundClip: 'initial', textShadow: 'none', display: 'inline-block' }}>✨</span>
          </h3>

            <div className="games-grid" key={`obj-${progressFilter}`}>
              {games.filter(g => g.game_mode === 'objects_only').filter(g => matchesProgressFilter(g)).slice(0, visibleObjectsGamesCount).map((game, filteredIndex) => { const realIndex = games.findIndex(g => g.id === game.id); return (
                <div key={game.id} style={getObjectsCardStyle(filteredIndex, gamesVisible)}>
                  <div
                    onClick={() => navigate(`/${game.id}`)}
                    draggable={hasRole(['admin', 'editor'])}
                    onDragStart={(e) => handleDragStart(e, game, realIndex)}
                    onDragOver={(e) => handleDragOver(e, realIndex)}
                    onDrop={(e) => handleDrop(e, realIndex)}
                    onDragEnd={() => { setDraggedGame(null); setDragOverIndex(null); }}
                    className="game-card-full"
                    style={{
                      background: game.cover_image_path 
                        ? `linear-gradient(to bottom, rgba(0,0,0,${userGameProgress[game.id]?.isCompleted ? '0.65' : '0.1'}) 0%, rgba(0,0,0,${userGameProgress[game.id]?.isCompleted ? '0.9' : '0.75'}) 100%), url(${API}/games/${game.id}/cover?thumb=true) center/cover`
                        : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      boxShadow: dragOverIndex === realIndex 
                        ? '0 0 0 4px #f59e0b, 0 25px 60px rgba(245, 158, 11, 0.5)'
                        : undefined
                    }}
                  >
                    {/* Progress badge — grouped at top-left */}
                    {userGameProgress[game.id] && (
                      <div className="progress-video-group">
                        {userGameProgress[game.id].isCompleted ? (
                          <div className="progress-badge-inline completed" title="Завершено">
                            <CheckCircle size={12} />
                            <span>Завершено</span>
                          </div>
                        ) : userGameProgress[game.id].hotspotsTotal > 0 && (
                          <div className="progress-badge-inline hotspots" title="Объекты">
                            <Search size={12} />
                            <span>{userGameProgress[game.id].hotspotsFound}/{userGameProgress[game.id].hotspotsTotal}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Admin actions — uniform group at bottom-right (inline with date) */}
                    {hasRole(['admin', 'editor']) && (
                      <div className="admin-actions-overlay">
                        <div
                          title={game.published ? 'Опубликовано' : 'Черновик'}
                          className="game-admin-btn"
                          style={{ color: game.published ? '#10b981' : '#f59e0b' }}
                        >
                          {game.published ? <Eye size={13} /> : <EyeOff size={13} />}
                        </div>
                        {hasRole(['admin']) && (
                          <button
                            onClick={(e) => handleDeleteGame(game.id, e)}
                            disabled={deleting === game.id}
                            className="game-admin-btn btn-delete"
                            title="Удалить"
                          >
                            {deleting === game.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          </button>
                        )}
                        <label onClick={e => e.stopPropagation()} className="game-admin-btn" title="Загрузить обложку">
                          {uploadingCover === game.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <>
                              <ImagePlus size={13} />
                              <input type="file" accept="image/*" style={{ display: 'none' }}
                                onChange={(e) => handleCoverUpload(game.id, e.target.files[0], e)} />
                            </>
                          )}
                        </label>
                      </div>
                    )}
                    
                    {/* Admin: centered game stats on cover */}
                    {hasRole(['admin', 'editor']) && (
                      <div
                        onClick={(e) => { e.stopPropagation(); navigate(`/${game.id}`); }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 15,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer',
                          textDecoration: 'none'
                        }}
                      >
                        <div style={{
                          background: 'rgba(0,0,0,0.6)',
                          backdropFilter: 'blur(8px)',
                          borderRadius: '16px',
                          padding: '12px 20px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                          border: '1px solid rgba(255,255,255,0.15)',
                          minWidth: '80px'
                        }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                            {game.players_count || 0}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                            {(game.players_count || 0) === 1 ? 'игрок' : (game.players_count || 0) < 5 ? 'игрока' : 'игроков'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Players badge for non-admin */}
                    {!hasRole(['admin', 'editor']) && game.players_count > 0 && (
                      <div className="players-badge-overlay">
                        {game.players_count}
                      </div>
                    )}
                    
                    {/* Content overlay */}
                    <div className="game-overlay">
                      <h3 className="game-title-overlay">{getTranslatedText(game.name_translations, language, game.name)}</h3>
                      <p className="game-date-overlay">{formatDate(game.published && game.published_at ? game.published_at : game.created_at)}</p>
                    </div>
                  </div>
                </div>
              ); })}
            </div>
            
            {games.filter(g => g.game_mode === 'objects_only').filter(g => matchesProgressFilter(g)).length > visibleObjectsGamesCount && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <button
                  onClick={() => { const newCount = visibleObjectsGamesCount + 9; setShownObjectsCardIndices(prev => { const s = new Set(prev); for(let i=visibleObjectsGamesCount; i<newCount; i++) s.add(i); return s; }); setVisibleObjectsGamesCount(newCount); }}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '25px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => { e.target.style.transform = 'scale(1.05)'; e.target.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)'; }}
                  onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.3)'; }}
                >
                  {t('game.showMore')} ({games.filter(g => g.game_mode === 'objects_only').filter(g => matchesProgressFilter(g)).length - visibleObjectsGamesCount})
                </button>
              </div>
            )}
        </div>
      </section>
      )}

      {/* Winners Section */}
      <section ref={winnersSectionRef} className="section winners-section">
        <div className="deco-circle deco-1" />
        <div className="deco-circle deco-2" />
        
        <div className="container">
          <div className="section-header">
            <div className="trophy-icon">
              <Trophy size={48} style={{ color: 'white' }} />
            </div>
            <h2 className="section-title purple">{t("sections.topPlayers")}</h2>
            <p className="section-subtitle purple">{t('sections.topByFirstPlaces')}</p>
          </div>

          {winners.length > 0 ? (
            <>
            <div className="winners-grid">
              {winners.map((winner, index) => {
                const avatarUrl = getWinnerAvatarUrl(winner);
                return (
                  <div key={winner.user_id} style={getWinnerCardStyle(index, winnersVisible)}>
                    <div 
                      className="winner-card" 
                      style={getPlaceStyle(index)}
                      onClick={() => navigate(`/profile/${winner.user_id}`)}
                    >
                      <div className="place-icon">{getPlaceIcon(index)}</div>
                      
                      {/* Avatar with optional country flag overlay */}
                      <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                        <img 
                          src={avatarUrl}
                          alt={winner.user_name}
                          className="winner-avatar"
                          style={{
                            width: index === 0 ? '90px' : '70px',
                            height: index === 0 ? '90px' : '70px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: index === 0 ? '4px solid #eab308' : '2px solid rgba(0,0,0,0.1)',
                            display: 'block'
                          }}
                        />
                        {winner.country && (
                          <span
                            className={`fi fi-${String(winner.country).toLowerCase()}`}
                            data-testid={`avatar-flag-${String(winner.country).toLowerCase()}`}
                            aria-label={`Country: ${winner.country}`}
                            style={{
                              position: 'absolute',
                              right: '-6%',
                              bottom: '-8%',
                              width: index === 0 ? 40 : 32,
                              height: index === 0 ? 30 : 24,
                              borderRadius: 3,
                              border: 'none',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </span>
                      
                      <h4 className="winner-name">{winner.user_name}</h4>
                      <div className="wins-count">
                        <Trophy size={16} />
                        <span>{winner.wins_count} {winner.wins_count === 1 ? t('sections.win') : winner.wins_count < 5 ? t('sections.wins2to4') : t('sections.wins5plus')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Show more winners button */}
            {allWinners.length > winners.length && (
              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginTop: "2rem" 
              }}>
                <button
                  onClick={() => {
                    const newCount = visibleWinnersCount + 10;
                    setVisibleWinnersCount(newCount);
                    setWinners(allWinners.slice(0, newCount));
                  }}
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 32px",
                    borderRadius: "25px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease"
                  }}
                  onMouseOver={(e) => e.target.style.transform = "scale(1.05)"}
                  onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                >
                  {t("game.showMore")} ({allWinners.length - winners.length})
                </button>
              </div>
            )}
            </>
          ) : (
            <div className="empty-state light">
              <Trophy size={56} />
              <p>{t('gameListUI.thereNoWinnersYet')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Recent Achievements Section */}
      {achievements.length > 0 && (
        <section ref={achievementsSectionRef} className="section achievements-section">
          <div className="container">
            <div className="section-header">
              <div className="medal-icon">
                <Award size={40} style={{ color: 'white' }} />
              </div>
              <h2 className="section-title">{t("sections.recentAchievements")}</h2>
              <p className="section-subtitle">{t('sections.recentPrizes')}</p>
            </div>

            <div className="achievements-grid">
              {achievements.map((achievement, index) => {
                const coverUrl = getGameCoverUrl(achievement);
                const avatarUrl = getAchievementAvatarUrl(achievement);
                
                return (
                  <div key={`${achievement.user_id}-${achievement.game_id}-${index}`} 
                       style={getAchievementCardStyle(index, achievementsVisible)}>
                    <div 
                      className="achievement-card-full"
                      onClick={() => navigate(`/games/${achievement.game_id}/user-results/${achievement.user_id}`)}
                      style={{
                        background: coverUrl 
                          ? `linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.85) 100%), url(${coverUrl}) center/cover`
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      {/* Place emoji - just emoji with white shadow */}
                      <span className="place-emoji-only">
                        {getPlaceEmoji(achievement.place)}
                      </span>
                      
                      {/* Content overlay */}
                      <div className="achievement-overlay">
                        <div className="achievement-user-row">
                          <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0, flexShrink: 0 }}>
                            <img 
                              src={avatarUrl}
                              alt={achievement.user_full_name}
                              className="user-avatar-small"
                              style={{ 
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '2px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                                display: 'block'
                              }}
                            />
                            {achievement.country && (
                              <span
                                className={`fi fi-${String(achievement.country).toLowerCase()}`}
                                data-testid={`avatar-flag-${String(achievement.country).toLowerCase()}`}
                                aria-label={`Country: ${achievement.country}`}
                                style={{
                                  position: 'absolute',
                                  right: '-6%',
                                  bottom: '-8%',
                                  width: 22,
                                  height: 16,
                                  borderRadius: 2,
                                  border: 'none',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  pointerEvents: 'none',
                                }}
                              />
                            )}
                          </span>
                          <div className="user-details">
                            <h4 className="user-name-full">{achievement.user_full_name}</h4>
                            <p className="game-name-full">{getTranslatedText(achievement.game_name_translations, language, achievement.game_name)}</p>
                          </div>
                        </div>
                        
                        <div className="stats-row">
                          <div className="stat-item">
                            <Target size={14} />
                            <span>{achievement.accuracy?.toFixed(1)}%</span>
                          </div>
                          <div className="stat-item">
                            <Calendar size={14} />
                            <span>{formatDate(achievement.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Info Sections */}
      {infoSections.length > 0 && (
        <section ref={infoSectionRef} className="section info-section">
          <div className="container narrow">
            <div className="section-header">
              <h2 className="section-title">{t("sections.howToPlay")}</h2>
              <p className="section-subtitle">{t("sections.learnMoreRules")}</p>
            </div>

            <div className="accordion-container">
              {infoSections.map((section, index) => (
                <div 
                  key={section.id} 
                  className="accordion-item"
                  style={{
                    opacity: 1,
                    transform: 'translateX(0)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <button onClick={() => toggleSection(section.id)} className="accordion-header">
                    <span>{getTranslatedText(section.title_translations, language, section.title)}</span>
                    <div className={`accordion-icon ${expandedSections[section.id] ? 'expanded' : ''}`}>
                      <ChevronDown size={20} />
                    </div>
                  </button>
                  <div className={`accordion-content ${expandedSections[section.id] ? 'open' : ''}`}>
                    <div className="accordion-inner" dangerouslySetInnerHTML={{ __html: getTranslatedText(section.content_translations, language, section.content) }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <SiteFooter />

      {/* Styles */}
      <style>{`
        .container { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
        .container.narrow { max-width: 800px; }
        .section { padding: 5rem 1.5rem; position: relative; overflow: hidden; }
        .section-header { text-align: center; margin-bottom: 3rem; }
        .section-title { font-size: clamp(2rem, 5vw, 3rem); font-weight: 800; color: #1f2937; margin-bottom: 0.75rem; }
        .section-title.purple { color: #581c87; }
        .section-subtitle { font-size: 1.1rem; color: #6b7280; max-width: 500px; margin: 0 auto; }
        .section-subtitle.purple { color: #7e22ce; }
        .admin-controls { display: flex; justify-content: center; gap: 1rem; margin-bottom: 2rem; }
        .loading-state { text-align: center; padding: 4rem; }
        .empty-state { text-align: center; padding: 4rem 2rem; background: white; border-radius: 24px; box-shadow: 0 10px 40px rgba(0,0,0,0.06); }
        .empty-state.light { background: rgba(255,255,255,0.9); }
        .empty-state svg { color: #d1d5db; margin-bottom: 1rem; }
        .empty-state p { color: #6b7280; font-size: 1.1rem; }
        
        /* Hero */
        .hero-section {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          position: relative;
          background: transparent;
          overflow: hidden;
          padding: 2rem;
        }
        .shapes-container { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
        .shape { position: absolute; border-radius: 50%; background: rgba(255,255,255,0.1); }
        .shape-1 { width: 700px; height: 700px; top: -300px; right: -200px; animation: floatSlow 25s ease-in-out infinite; }
        .shape-2 { width: 500px; height: 500px; bottom: -150px; left: -150px; animation: floatSlow 20s ease-in-out infinite reverse; }
        .shape-3 { width: 350px; height: 350px; top: 30%; left: 10%; animation: floatSlow 22s ease-in-out infinite 3s; }
        .shape-4 { width: 250px; height: 250px; top: 15%; right: 15%; animation: floatSlow 18s ease-in-out infinite 1s; }
        .shape-5 { width: 180px; height: 180px; bottom: 20%; right: 25%; animation: floatSlow 15s ease-in-out infinite 2s; }
        
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg) scale(1); }
          33% { transform: translateY(-40px) rotate(5deg) scale(1.05); }
          66% { transform: translateY(20px) rotate(-3deg) scale(0.98); }
        }
        
        .hero-content { text-align: center; color: white; z-index: 1; max-width: 900px; animation: heroFadeIn 1.2s ease-out; }
        .hero-title { font-size: clamp(3rem, 10vw, 5.5rem); font-weight: 900; margin-bottom: 1.5rem; text-shadow: 0 8px 30px rgba(0,0,0,0.3); }
        .hero-subtitle { font-size: clamp(1.1rem, 3vw, 1.5rem); margin-bottom: 2.5rem; opacity: 0.95; line-height: 1.7; }
        .hero-buttons { display: flex; gap: 1.5rem; justify-content: center; flex-wrap: wrap; }
        
        .btn-play {
          display: inline-flex !important; align-items: center !important; justify-content: center !important;
          background: white !important; color: #667eea !important;
          box-sizing: border-box !important;
          height: 60px !important; min-height: 60px !important; max-height: 60px !important;
          padding: 0 2.5rem !important; font-size: 1.15rem !important; font-weight: 700 !important;
          line-height: 1 !important;
          border-radius: 60px !important;
          box-shadow: 0 15px 50px rgba(10,71,151,0.45) !important;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }
        .btn-play:hover { transform: translateY(-8px) scale(1.08) !important; box-shadow: 0 25px 70px rgba(10,71,151,0.55) !important; }

        .btn-invite-hero {
          display: inline-flex !important; align-items: center !important; justify-content: center !important;
          background: linear-gradient(135deg, #0a4797 0%, #1e5fb8 100%) !important; color: white !important;
          box-sizing: border-box !important;
          height: 60px !important; min-height: 60px !important; max-height: 60px !important;
          padding: 0 2.5rem !important; font-size: 1.15rem !important; font-weight: 700 !important;
          line-height: 1 !important;
          border: none !important; border-radius: 60px !important; cursor: pointer !important;
          box-shadow: 0 15px 50px rgba(10,71,151,0.45) !important;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }
        .btn-invite-hero:hover { transform: translateY(-8px) scale(1.08) !important; box-shadow: 0 25px 70px rgba(10,71,151,0.55) !important; }
        
        .btn-login {
          background: transparent !important; color: white !important;
          border: 2px solid rgba(255,255,255,0.6) !important;
          padding: 1.2rem 3rem !important; font-size: 1.15rem !important; font-weight: 600 !important;
          border-radius: 60px !important; transition: all 0.3s ease !important;
        }
        .btn-login:hover { background: rgba(255,255,255,0.2) !important; border-color: white !important; }
        
        .scroll-indicator { position: absolute; bottom: 2.5rem; cursor: pointer; animation: bounce 2.5s ease-in-out infinite; opacity: 0.9; }
        @keyframes heroFadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        
        /* Games - Full card cover */
        .games-section { background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%); }
        .games-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 1.5rem; }
        
        .game-card-full {
          height: 220px;
          border-radius: 20px;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          box-shadow: 0 15px 50px rgba(0,0,0,0.15);
          transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .game-card-full:hover {
          transform: translateY(-12px) scale(1.03) !important;
          box-shadow: 0 30px 70px rgba(0,0,0,0.25) !important;
        }
        
        /* Progress filter chips next to section title */
        .games-header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.75rem 1.5rem;
          margin-bottom: 1rem;
        }
        .games-header .section-title { margin: 0; }

        .progress-filter-chips {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
          max-width: 100%;
          justify-content: center;
        }
        .progress-chip {
          appearance: none;
          border: 1px solid transparent;
          background: transparent;
          color: #4b5563;
          font-weight: 600;
          font-size: 0.85rem;
          padding: 7px 14px;
          border-radius: 999px;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
          white-space: nowrap;
          line-height: 1;
        }
        @media (max-width: 640px) {
          .games-header { gap: 0.6rem; }
          .progress-filter-chips { gap: 2px; }
          .progress-chip { font-size: 0.78rem; padding: 6px 10px; }
        }
        .progress-chip:hover:not(.is-active) {
          background: rgba(102, 126, 234, 0.08);
          color: #1f2937;
        }
        .progress-chip.is-active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.35);
        }
        .progress-chip-count {
          display: inline-block;
          margin-left: 5px;
          min-width: 1.4em;
          text-align: center;
          font-size: 0.72em;
          font-weight: 600;
          opacity: 0.55;
        }
        .progress-chip.is-active .progress-chip-count { opacity: 0.85; }

        @keyframes gridFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .games-grid { animation: gridFadeIn 0.35s ease-out both; }

        /* Unified admin action buttons row (bottom-right, inline with date) */
        .admin-actions-overlay {
          position: absolute;
          bottom: 1.25rem;
          right: 1rem;
          display: flex;
          flex-direction: row;
          gap: 6px;
          z-index: 6;
        }
        .admin-actions-overlay .game-admin-btn {
          width: 26px;
          height: 26px;
          padding: 0;
          border-radius: 7px;
          background: rgba(255,255,255,0.92);
          color: #1f2937;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: transform 0.2s ease, background 0.2s ease;
          font-size: 0.85rem;
          font-weight: 700;
          line-height: 1;
        }
        .admin-actions-overlay .game-admin-btn:hover { transform: scale(1.12); }
        .admin-actions-overlay .game-admin-btn.btn-delete {
          background: rgba(239, 68, 68, 0.92); color: white;
        }
        .admin-actions-overlay .game-admin-btn.btn-delete:hover { background: #dc2626; }
        .admin-actions-overlay .game-admin-btn svg { width: 13px; height: 13px; }
        
        /* Legacy classnames kept as no-op shells for backward compat */
        .upload-btn-overlay, .delete-btn-overlay, .status-btn-overlay { all: unset; }

        /* Progress/video group at top-left (under players badge) */
        .progress-video-group {
          position: absolute;
          top: calc(1rem + 36px);
          left: 1rem;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 6px;
          z-index: 20;
          flex-wrap: wrap;
          max-width: calc(100% - 2rem);
        }
        
        .interactive-video-icon {
          width: 28px; height: 28px; border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 8px rgba(99,102,241,0.5);
          border: 2px solid rgba(255,255,255,0.3);
          cursor: pointer;
          flex-shrink: 0;
        }
        
        .progress-badge-inline {
          border-radius: 14px;
          padding: 4px 9px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.75rem;
          font-weight: 700;
          color: white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
          height: 24px;
          box-sizing: border-box;
        }
        .progress-badge-inline.panoramas { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .progress-badge-inline.hotspots { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
        .progress-badge-inline.completed { background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); }

        .players-badge-overlay {
          position: absolute; top: 1rem; left: 1rem;
          background: rgba(0,0,0,0.7); color: white;
          padding: 0.35rem 0.75rem; border-radius: 15px; font-size: 0.8rem; font-weight: 600;
          display: flex; align-items: center; gap: 0.3rem; backdrop-filter: blur(10px);
          height: 28px; box-sizing: border-box;
        }
        
        .points-badge-overlay {
          position: absolute; top: 1rem;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white;
          padding: 0.35rem 0.75rem; border-radius: 15px; font-size: 0.8rem; font-weight: 600;
          display: flex; align-items: center; gap: 0.3rem;
          height: 28px; box-sizing: border-box;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        
        .medals-overlay {
          position: absolute;
          top: 0.5rem;
          right: 0.6rem;
          display: flex;
          flex-direction: row;
          gap: 0.3rem;
          z-index: 4;
        }
        
        .medal {
          display: inline-flex;
          align-items: center;
          transition: transform 0.3s ease, filter 0.3s ease, opacity 0.3s ease;
        }

        .medal img {
          display: block;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.45));
        }

        .medal-empty {
          opacity: 0.45;
          cursor: default;
        }

        .medal-empty img {
          filter: grayscale(95%) brightness(0.7) drop-shadow(0 2px 3px rgba(0,0,0,0.4));
        }

        .medal-occupied {
          opacity: 1;
          cursor: pointer;
          animation: medalPulse 2.5s ease-in-out infinite;
        }

        .medal-occupied:hover {
          transform: scale(1.18) translateY(-2px);
        }

        .medal-occupied:hover img {
          filter: drop-shadow(0 4px 12px rgba(255,215,0,0.55)) brightness(1.1);
        }
        
        @keyframes medalPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        .game-overlay { padding: 1.25rem 1.25rem 1rem; }
        .game-title-overlay {
          font-size: 1.4rem; font-weight: 700; color: white; margin-bottom: 0.3rem;
          text-shadow: 0 2px 10px rgba(0,0,0,0.5);
        }
        .game-date-overlay { font-size: 0.9rem; color: rgba(255,255,255,0.8); padding-right: 100px; }
        
        /* Winners */
        .winners-section { background: linear-gradient(135deg, #fdf4ff 0%, #fae8ff 50%, #f5f3ff 100%); }
        .deco-circle { position: absolute; border-radius: 50%; background: linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1)); }
        .deco-1 { width: 600px; height: 600px; top: -200px; right: -150px; animation: floatSlow 20s ease-in-out infinite; }
        .deco-2 { width: 400px; height: 400px; bottom: -100px; left: -100px; animation: floatSlow 18s ease-in-out infinite reverse; }
        
        .trophy-icon, .medal-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 100px; height: 100px; border-radius: 50%; margin-bottom: 1.5rem;
          box-shadow: 0 20px 60px rgba(245, 158, 11, 0.4);
          animation: iconPulse 3s ease-in-out infinite;
        }
        .trophy-icon { background: linear-gradient(145deg, #fbbf24, #f59e0b); }
        .medal-icon { background: linear-gradient(145deg, #667eea, #764ba2); box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4); }
        
        @keyframes iconPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
        
        .winners-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1.5rem; max-width: 900px; margin: 0 auto; }
        .winner-card {
          border-radius: 24px; padding: 1.5rem 1rem; text-align: center;
          transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
          cursor: pointer;
        }
        .winner-card:hover { transform: translateY(-12px) scale(1.08) !important; }
        .place-icon { margin-bottom: 0.75rem; }
        .winner-avatar {
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1rem; color: white; font-weight: 800;
          box-shadow: 0 10px 30px rgba(0,0,0,0.15); transition: all 0.3s ease;
        }
        .winner-card:hover .winner-avatar { transform: scale(1.1); }
        .winner-name { font-weight: 700; color: #1f2937; margin-bottom: 0.5rem; font-size: 0.95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .wins-count { color: #7c3aed; font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; justify-content: center; gap: 0.4rem; }
        
        /* Achievements */
        .achievements-section { background: linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%); }
        .achievements-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 1.5rem; }
        
        .achievement-card-full {
          height: 220px;
          border-radius: 20px;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          box-shadow: 0 15px 50px rgba(0,0,0,0.15);
          transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .achievement-card-full:hover {
          transform: translateY(-12px) scale(1.03) !important;
          box-shadow: 0 30px 70px rgba(0,0,0,0.25) !important;
        }
        
        .place-emoji-only {
          position: absolute;
          top: 1rem;
          left: 1rem;
          font-size: 2.5rem;
          text-shadow: 0 2px 8px rgba(255,255,255,0.9), 0 4px 15px rgba(0,0,0,0.3);
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        }
        
        .achievement-overlay { padding: 1.25rem; }
        
        .achievement-user-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
        
        .user-avatar-small {
          width: 44px; height: 44px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: white; font-weight: 700; font-size: 1rem; flex-shrink: 0;
          border: 2px solid rgba(255,255,255,0.3);
          box-shadow: 0 4px 15px rgba(0,0,0,0.3);
        }
        
        .user-details { flex: 1; min-width: 0; }
        .user-name-full {
          font-weight: 700; color: white; font-size: 1.05rem;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          text-shadow: 0 2px 8px rgba(0,0,0,0.5);
        }
        .game-name-full { font-size: 0.85rem; color: rgba(255,255,255,0.8); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        
        .stats-row { display: flex; gap: 1.25rem; }
        .stat-item { display: flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; color: rgba(255,255,255,0.9); font-weight: 600; }
        .stat-item svg { color: rgba(255,255,255,0.7); }
        
        /* Accordion */
        .info-section { background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); }
        .accordion-container { display: flex; flex-direction: column; gap: 0.75rem; }
        .accordion-item { background: white; border-radius: 16px; box-shadow: 0 4px 25px rgba(0,0,0,0.06); overflow: hidden; transition: all 0.4s ease; }
        .accordion-item:hover { box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
        .accordion-header {
          width: 100%; display: flex; justify-content: space-between; align-items: center;
          padding: 1.5rem 1.75rem; background: none; border: none; cursor: pointer;
          text-align: left; font-weight: 600; font-size: 1.1rem; color: #1f2937;
        }
        .accordion-header:hover { background: #f9fafb; }
        .accordion-icon { background: #f3f4f6; border-radius: 50%; padding: 0.4rem; transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); color: #6b7280; }
        .accordion-icon.expanded { background: #667eea; color: white; transform: rotate(180deg); }
        .accordion-content { max-height: 0; overflow: hidden; transition: max-height 0.5s cubic-bezier(0.16, 1, 0.3, 1); }
        .accordion-content.open { max-height: 2000px; }
        .accordion-inner { padding: 0 1.75rem 1.75rem; border-top: 1px solid #f3f4f6; padding-top: 1.25rem; color: #4b5563; line-height: 1.9; }
        
        /* Footer */
        .footer { padding: 4rem 1.5rem; background: linear-gradient(135deg, #1f2937 0%, #111827 100%); color: white; text-align: center; }
        .footer h3 { font-size: 1.75rem; font-weight: 800; margin-bottom: 0.75rem; }
        .footer p { color: rgba(255,255,255,0.6); font-size: 0.95rem; }
        
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        /* Mobile */
        @media (max-width: 768px) {
          .shape { opacity: 0.5; }
          .games-grid, .achievements-grid { grid-template-columns: 1fr; }
          .game-card-full, .achievement-card-full { height: 200px; }
          .winners-grid { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
          .hero-title { font-size: 2.5rem; }
          .hero-buttons { flex-direction: column; }
          .btn-play, .btn-login, .btn-invite-hero { width: 100%; }
          .section { padding: 4rem 1rem; }
          .trophy-icon, .medal-icon { width: 80px; height: 80px; }
          .trophy-icon svg, .medal-icon svg { width: 36px; height: 36px; }
          .place-emoji-only { font-size: 2rem; }
        }
      `}</style>
    </div>
  );
};

export default GameList;
